import React, { useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { PageHero, SectionLabel } from '../components/shared';
import { LeadForm } from '../components/LeadForm';
const propertyTypes = ['Restaurant or Bar', 'Retail', 'Office', 'Warehouse', 'Property Management', 'School or Church', 'Home', 'Other'];
const concerns = ['Break ins', 'Employee access', 'Cameras', 'After hours activity', 'Old system repair', 'Multi location control', 'Smart home security'];
export function Assessment() {
  const [a, setA] = useState({
    propertyType: 'Restaurant or Bar',
    doors: '3 to 5',
    cameras: '6 to 10',
    existing: 'Yes',
    concern: 'After hours activity'
  });
  const result = useMemo(() => {
    const isHome = a.propertyType === 'Home';
    const access = a.doors !== '0 to 1' && !isHome;
    const cameraHeavy = a.cameras === '6 to 10' || a.cameras === '11 plus';
    const repair = a.existing === 'Yes' && a.concern === 'Old system repair';
    return {
      title: isHome ? 'Smart Home Protection Plan' : access ? 'Commercial Command Plan' : 'Core Security Plan',
      summary: repair ? 'Your property likely needs a system review, repair plan, and upgrade path before adding new devices.' : cameraHeavy ? 'Your property likely needs planned camera coverage, intrusion detection, and app based alerts tied to daily operations.' : 'Your property likely needs a clean alarm and camera foundation with room to expand as needs grow.',
      recommendations: [access ? 'Access control for priority doors' : 'Smart locks or controlled entry where needed', cameraHeavy ? 'Camera map for interior, exterior, and blind spots' : 'Core camera coverage for entrances and key areas', 'Intrusion detection for after hours activity', a.existing === 'Yes' ? 'Existing system audit and upgrade review' : 'New system design with future expansion']
    };
  }, [a]);
  return <>
      <PageHero eyebrow="Free assessment" title="Know what your property needs before you spend a dollar." subtitle="Answer a few quick questions and we will preview the right plan for you. A specialist will follow up to refine and price it." data-id="element-163" />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-id="element-164">
        <div className="grid gap-10 lg:grid-cols-[.92fr_1.08fr] lg:items-start" data-id="element-165">
          <div data-id="element-166">
            <SectionLabel data-id="element-167">Step 1 — Tell us about your property</SectionLabel>
            <div className="mt-6 grid gap-5 rounded-[2rem] bg-white p-6 shadow-sm ring-1 ring-slate-200 sm:grid-cols-2" data-id="element-168">
              <label className="block" data-id="element-169">
                <span className="mb-2 block text-sm font-black text-slate-700" data-id="element-170">
                  Property type
                </span>
                <select value={a.propertyType} onChange={e => setA({
                ...a,
                propertyType: e.target.value
              })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-171">
                  {propertyTypes.map(t => <option key={t} data-id="element-172">{t}</option>)}
                </select>
              </label>
              <label className="block" data-id="element-173">
                <span className="mb-2 block text-sm font-black text-slate-700" data-id="element-174">
                  Controlled doors
                </span>
                <select value={a.doors} onChange={e => setA({
                ...a,
                doors: e.target.value
              })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-175">
                  {['0 to 1', '2', '3 to 5', '6 plus'].map(t => <option key={t} data-id="element-176">{t}</option>)}
                </select>
              </label>
              <label className="block" data-id="element-177">
                <span className="mb-2 block text-sm font-black text-slate-700" data-id="element-178">
                  Camera needs
                </span>
                <select value={a.cameras} onChange={e => setA({
                ...a,
                cameras: e.target.value
              })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-179">
                  {['0 to 2', '3 to 5', '6 to 10', '11 plus'].map(t => <option key={t} data-id="element-180">{t}</option>)}
                </select>
              </label>
              <label className="block" data-id="element-181">
                <span className="mb-2 block text-sm font-black text-slate-700" data-id="element-182">
                  Existing system?
                </span>
                <select value={a.existing} onChange={e => setA({
                ...a,
                existing: e.target.value
              })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-183">
                  {['Yes', 'No', 'Not sure'].map(t => <option key={t} data-id="element-184">{t}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2" data-id="element-185">
                <span className="mb-2 block text-sm font-black text-slate-700" data-id="element-186">
                  Main concern
                </span>
                <select value={a.concern} onChange={e => setA({
                ...a,
                concern: e.target.value
              })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none ring-sky-300 focus:ring-4" data-id="element-187">
                  {concerns.map(t => <option key={t} data-id="element-188">{t}</option>)}
                </select>
              </label>
            </div>

            <div className="mt-6 rounded-[2rem] bg-slate-950 p-6 text-white" data-id="element-189">
              <p className="text-xs font-black uppercase tracking-wider text-sky-300" data-id="element-190">
                Suggested plan preview
              </p>
              <h3 className="mt-2 text-2xl font-black" data-id="element-191">{result.title}</h3>
              <p className="mt-3 leading-7 text-slate-300" data-id="element-192">{result.summary}</p>
              <div className="mt-5 grid gap-2" data-id="element-193">
                {result.recommendations.map(r => <div key={r} className="flex gap-3 rounded-2xl bg-white/[0.06] p-4" data-id="element-194">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-sky-300" data-id="element-195" />
                    <span className="text-sm font-bold text-slate-200" data-id="element-196">
                      {r}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          <div data-id="element-197">
            <SectionLabel data-id="element-198">Step 2 — Send it to a specialist</SectionLabel>
            <div className="mt-6" data-id="element-199">
              <LeadForm defaultInterest={result.title} title="Get my full assessment" subtitle="We will review your answers and reach out with a tailored plan and pricing." data-id="element-200" />
            </div>
          </div>
        </div>
      </section>
    </>;
}